import React, { useCallback } from 'react';
import moment from 'moment';
import { DueDateBadge } from '@atlassian/trello-canonical-components/src/card-front';
import styles from './DueCell.less';
import classNames from 'classnames';
import { CellWrapper } from './CellWrapper';
import { Checkbox } from '@trello/nachos/checkbox';
import { Tooltip } from '@trello/nachos/tooltip';
import { useUpdateCardDueCompleteMutation } from './UpdateCardDueCompleteMutation.generated';
import {
  useDatePickerPopover,
  DueDatePickerPopover,
} from 'app/src/components/DueDatePickerPopover/';
import { Analytics } from '@trello/atlassian-analytics';
import { TableTestClasses } from '@trello/test-ids';

type formatDateCallback = (
  momentDate: moment.Moment,
  hideYear?: boolean,
) => string;

const formatTooltip = (momentDate: moment.Moment) => {
  return momentDate.format('dddd, MMMM Do, YYYY');
};

const formatBadge = (momentDate: moment.Moment, hideYear: boolean) => {
  return momentDate.format(hideYear ? 'llll' : 'll');
};

const formatDate = (date: string, formatType: formatDateCallback) => {
  const momentDate = moment(new Date(date));
  const now = moment();

  const isAmbiguousMonth = Math.abs(now.diff(momentDate, 'months')) > 9;
  const hideYear = momentDate.isSame(now, 'year') && !isAmbiguousMonth;

  return formatType(momentDate, hideYear);
};

interface DueCellProps {
  due?: string | null;
  dueReminder?: number | null;
  dueComplete?: boolean;
  editable: boolean;
  idCard: string;
  idOrg?: string;
}

export const DueCell: React.FunctionComponent<DueCellProps> = ({
  due,
  dueComplete,
  dueReminder,
  editable,
  idCard,
  idOrg,
}) => {
  const [sendCardRequest] = useUpdateCardDueCompleteMutation();

  const { triggerRef, toggle, popoverProps, hide } = useDatePickerPopover();

  const toggleDatePicker = useCallback(
    (e: React.MouseEvent) => {
      const eventTarget = e.target as HTMLElement;
      if (editable && eventTarget.closest('.dueCompleteCheckbox') === null) {
        Analytics.sendClickedButtonEvent({
          source: 'multiBoardTableViewScreen',
          buttonName: 'dueCellButton',
          containers: {
            organization: {
              id: idOrg,
            },
          },
        });

        toggle();
      }
    },
    [editable, idOrg, toggle],
  );

  return (
    <>
      <CellWrapper
        ref={triggerRef}
        // eslint-disable-next-line react/jsx-no-bind
        onClick={(e: React.MouseEvent) => toggleDatePicker(e)}
        clickable={editable}
        isEmpty={!due}
      >
        <div className={styles.dueCell}>
          {due && (
            <>
              <Checkbox
                className={classNames(styles.checkBox, 'dueCompleteCheckbox')}
                isDisabled={!editable}
                isChecked={dueComplete}
                testClass={
                  dueComplete
                    ? TableTestClasses.DueDateCheckboxChecked
                    : TableTestClasses.DueDateCheckboxUnchecked
                }
                label={
                  <div className={styles.hiddenLabel}>
                    {formatDate(due, formatTooltip)}
                  </div>
                }
                // eslint-disable-next-line react/jsx-no-bind
                onChange={async () => {
                  const newDueComplete = !dueComplete;

                  const traceId = Analytics.startTask({
                    taskName: 'edit-card/dueComplete',
                    source: 'multiBoardTableViewScreen',
                  });

                  try {
                    await sendCardRequest({
                      variables: {
                        traceId,
                        cardId: idCard,
                        dueComplete: newDueComplete,
                      },
                      optimisticResponse: {
                        __typename: 'Mutation',
                        updateCardDueComplete: {
                          id: idCard,
                          dueComplete: newDueComplete,
                          __typename: 'Card',
                        },
                      },
                    });

                    Analytics.sendUpdatedCardFieldEvent({
                      field: 'dueComplete',
                      source: 'multiBoardTableViewScreen',
                      containers: {
                        card: { id: idCard },
                      },
                      attributes: {
                        taskId: traceId,
                      },
                    });

                    Analytics.taskSucceeded({
                      taskName: 'edit-card/dueComplete',
                      traceId,
                      source: 'multiBoardTableViewScreen',
                    });
                  } catch (error) {
                    throw Analytics.taskFailed({
                      taskName: 'edit-card/dueComplete',
                      traceId,
                      source: 'multiBoardTableViewScreen',
                      error,
                    });
                  }
                }}
              />
              <Tooltip
                content={formatDate(due, formatTooltip)}
                hideTooltipOnMouseDown
              >
                <DueDateBadge
                  dueDate={new Date(due)}
                  className={styles.dueDateBadge}
                  isComplete={dueComplete}
                >
                  {formatDate(due, formatBadge)}
                </DueDateBadge>
              </Tooltip>
            </>
          )}
        </div>
      </CellWrapper>
      {editable && (
        <DueDatePickerPopover
          due={due}
          dueReminder={dueReminder}
          idCard={idCard}
          popProps={popoverProps}
          hidePopover={hide}
        />
      )}
    </>
  );
};
